import type { ReactNode } from "react";

import {
  detailActions,
  detailCallout,
  detailSentence,
  detailSourceLine,
  detailTimeline,
} from "../exception-detail.ts";
import { badgeLabel, formatClock, stateClassOf, type TriageRow } from "../triage.ts";
import { StateBadge } from "./StateBadge.tsx";

/**
 * The selected row, in full: what it is, why it is here, what it is made of, and what to do about it.
 *
 * The right-hand 420px of the exceptions board at 1440, and an overlay over the table below 1240 —
 * the design's own breakpoint, and the reason the panel is a component rather than a column: at 1024
 * it is the same panel sliding over the same table, not a second layout.
 *
 * Every measurement is read from the rendered design rather than its source. The panel is 420 wide
 * with a 1px left border and a 980px cap; its header is 14px by 18px with a 10px gap and sticks to
 * the top; the body is 18px of padding and a 16px gap between five sections; the field table is a
 * `minmax(110px, .9fr) minmax(0, 1.4fr)` grid with a 10px gap and `9px 12px` of padding per row,
 * ringed rather than bordered so the 12px radius clips cleanly; each action is a 44px button with a
 * 12px radius above an 11.5px note on a 16px line; timeline rows are `14px minmax(0, 1fr)` with a
 * 10px gap and a 10px dot haloed 3px in 13% of its own colour.
 *
 * What it does not show is as deliberate. There is no place name on any row, no "3 of 3" on a
 * dispatch attempt and no name against a conflicting change, because DEL-08 publishes no address,
 * DSP-05 publishes no maximum and neither 409 publishes an actor. A panel that filled those in would
 * be inventing the three facts an operator would most want to rely on.
 */

export interface ExceptionDetailProps {
  readonly row: TriageRow;
  /** The board's clock, so the countdowns here and in the table agree. */
  readonly now: number;
  /**
   * True when this operator holds no scope that could run any of these actions.
   *
   * Every action stays visible and disabled, with the scope named. Hiding them would tell a viewer
   * that nothing can be done about the row, which is a different and false statement.
   */
  readonly viewOnly: boolean;
  /**
   * Whether a row has been clicked.
   *
   * Only overlay mode reads it. Docked, the panel is a column and is always there; as an overlay it
   * covers the table, so it opens on a click and closes on the button in its header. Which of the two
   * is in force is a media query rather than a prop, because the design decides it from the width.
   */
  readonly open: boolean;
  /** Present only in overlay mode, where the design shows a back button in the header. */
  readonly onClose?: () => void;
}

export function ExceptionDetail({
  row,
  now,
  viewOnly,
  open,
  onClose,
}: ExceptionDetailProps): ReactNode {
  const callout = detailCallout(row, now);
  const actions = detailActions(row);
  const timeline = detailTimeline(row, now);

  return (
    <aside className={open ? "detail detail--open" : "detail"} aria-label="Exception detail">
      <div className="detail__head">
        {onClose === undefined ? null : (
          <button type="button" className="detail__back" aria-label="Close" onClick={onClose}>
            <span aria-hidden="true">arrow_back</span>
          </button>
        )}
        <span className="detail__ident">
          <span className="detail__id">{row.id}</span>
          <span className="detail__source">{detailSourceLine(row)}</span>
        </span>
        <StateBadge
          code={row.code}
          status={row.status}
          classKey={stateClassOf(row)}
          label={badgeLabel(row)}
        />
      </div>

      <div className="detail__body">
        <div className="detail__headline">
          <strong className="detail__what">{row.what}</strong>
          <span className="detail__sentence">{detailSentence(row)}</span>
        </div>

        <div className={`detail__callout detail__callout--${callout.tone}`}>
          <span aria-hidden="true" className="detail__callout-glyph">
            {callout.glyph}
          </span>
          <span className="detail__callout-text">
            <strong className="detail__callout-title">{callout.title}</strong>
            <span className="detail__callout-body">{callout.body}</span>
          </span>
        </div>

        <div className="detail__fields">
          {row.fields.map((field) => (
            <div key={field.name} className="detail__field">
              <span className="detail__field-name">{field.name}</span>
              <span
                className={
                  field.mono === true
                    ? "detail__field-value detail__field-value--mono"
                    : "detail__field-value"
                }
              >
                {field.instant === true
                  ? `in ${formatClock(Date.parse(field.value) - now)}`
                  : field.value}
              </span>
            </div>
          ))}
        </div>

        {row.evidence === undefined || row.evidence.length === 0 ? null : (
          <div className="detail__evidence">
            <span className="detail__section-title">Evidence</span>
            <div className="detail__evidence-grid">
              {row.evidence.map((reference) => (
                <div key={reference} className="detail__evidence-tile">
                  <span aria-hidden="true" className="detail__evidence-glyph">
                    image
                  </span>
                  <span className="detail__evidence-ref">{reference}</span>
                </div>
              ))}
            </div>
            <span className="detail__footnote">
              References only. Images load through FIL-04 secure download.
            </span>
          </div>
        )}

        <div className="detail__actions">
          <span className="detail__section-title">What happens next</span>
          {actions.map((action) => (
            <div key={action.label} className="detail__action">
              <button
                type="button"
                disabled={viewOnly}
                className={
                  action.primary === true
                    ? "detail__action-button detail__action-button--primary"
                    : "detail__action-button"
                }
              >
                <span aria-hidden="true" className="detail__action-glyph">
                  {action.glyph}
                </span>
                <span className="detail__action-label">{action.label}</span>
                <span className="detail__action-op">{action.operation}</span>
              </button>
              <span
                className={
                  viewOnly
                    ? "detail__note detail__note--scope"
                    : action.missing === true
                      ? "detail__note detail__note--missing"
                      : "detail__note"
                }
              >
                {viewOnly
                  ? "Needs delivery-exception:write (or the listed scope). Your role can view only."
                  : action.note}
              </span>
            </div>
          ))}
        </div>

        <div className="detail__timeline">
          <span className="detail__section-title">Timeline</span>
          {timeline.map((event) => (
            <div key={event.text} className="detail__event">
              <span aria-hidden="true" className={`detail__dot detail__dot--${event.tone}`} />
              <span className="detail__event-text">
                <span className="detail__event-title">{event.text}</span>
                <span className="detail__event-at">{event.at}</span>
              </span>
            </div>
          ))}
          <span className="detail__footnote">
            Built from openedAt, updatedAt and retryAt. A full audit history needs AUD-01, not yet
            wired to this screen.
          </span>
        </div>
      </div>
    </aside>
  );
}
